import { ActionsDrop } from "components/actions-drop"
import { Button } from "components/Button/Button"
import { DataTable, TableCol } from "components/DataTable/DataTable"
import { DialogModal } from "components/dialog-modal/dialog-modal"
import { useLangContext } from "context/Lang"
import { BoxesIcon, Plus } from "lucide-react"
import { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate } from "react-router-dom"
import { routes } from "routes"
import { AsyncProcess, } from "services/AsyncProcess"
import { setToast } from "storeRedux/slices/toast"
import { ReplaceWords } from "utils/ReplaceWords"
import { ResponsiblePage } from "./responsible-page"
import { Loader } from "components/Loader/Loader"
import { Message } from "primereact/message"
import { useSelect } from "@react-three/drei"
import { RootStateType } from "storeRedux/store"
import { PreviewBuilding } from "./preview-building"
import { UserTypes } from "assets/Roles"


export const Buildings = () => {
    const { gW } = useLangContext();
    const dispatch = useDispatch();

    // States
    const [loading, setLoading] = useState<boolean>(true);
    const [buildings, setBuildings] = useState<Building[]>([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [lazyParams, setLazyParams] = useState({ first: 0, rows: 10, page: 1 });

    const [openResponsibleModal, setOpenResponsibleModal] = useState<boolean>(false);

    const [responsibles, setResponsible] = useState<ResponsibleType[]>([]);

    const [permission, setPermission] = useState<boolean>(true);

    const [responsiblesIds, setResponsiblesIds] = useState<number[]>([]);
    const [saving, setSaving] = useState<boolean>(false);

    const [isPreviewOpen, setIsPreviewOpen] = useState<EditorItemType|null>(null);

    const { user } = useSelector((state: RootStateType) => state.auth);


    const navigate = useNavigate();


    const handleSave = () => {
        setSaving(true);
        AsyncProcess({
            type: "post",
            url: routes.api.responsible,
            data: { responsibleIds: responsiblesIds },
            setError: () => { setSaving(false) },
            effectProcess: [(res) => {
                setSaving(false);
                if (res?.success === false) {
                    dispatch(setToast({ detail: gW(res?.message), severity: "error" }));
                    setPermission(false);
                } else {
                    dispatch(setToast({ detail: gW("saved-success"), severity: "success" }));
                    setOpenResponsibleModal(false);
                }
            }]
        });
    };

    const handleSaveResponsibles = () => {
        handleSave();
    }

    const getResponsibles = () => {
        AsyncProcess({
            type: "get",
            url: routes.api.responsible,

            setError: () => { setLoading(false); },
            effectProcess: [(res) => {
                setLoading(false);
                if (res?.success !== false) {
                    setResponsible(res?.data);
                    setResponsiblesIds(res?.data?.map((r: ResponsibleType) => r.user.id));
                }
            }]
        });
    }



    const fetchBuildings = async () => {
        setLoading(true);
        await AsyncProcess({
            type: "get",
            url: routes.api.buildings,
            setError: (err) => {
                setLoading(false);
                dispatch(
                    setToast({
                        detail: gW(err?.error || "happen-error", err?.words),
                        severity: "error",
                    })
                );
            },
            effectProcess: [(res) => {


                if (res?.success === false) {
                    setLoading(false);
                    dispatch(
                        setToast({
                            detail: gW(res?.message),
                            severity: "error",
                        })
                    )
                    setPermission(false);
                } else {
                    getResponsibles()
                    setBuildings(res.data || []);
                    setTotalRecords(res?.data?.length || 0);
                }
            }],
        });


    };


    const onPageChange = (event: any) => {
        setLazyParams({
            first: event.first,
            rows: event.rows,
            page: event.page + 1
        });
    };
    useEffect(() => {
        fetchBuildings();
    }, []);

    // تعريف الأعمدة للجدول
    const columns: TableCol<Building>[] = [
        {
            label: gW("building-id"),
            name: "id",
        },
        {
            label: gW("building-name"), // تأكد من وجود المفتاح في ملف الترجمة
            name: "title",
        },
        {
            label: gW("unit-name"),
            name: "unitName",
        },
        {
            label: gW("view-building"),
            cell: (building: Building) => (
                <>
                    {!building?.activeEditor?.id?gW("no-active-building"):<Button variant="ghost" onClick={()=>setIsPreviewOpen({...building.activeEditor!,building})} > <BoxesIcon/> {gW("view-active-building")} </Button>}
                </>
            ),
        },
        {
            label: gW("actions"),
            cell: (building: Building) => (
                <ActionsDrop
                    
                    // onEdit={() => {
                    // }}

                    onGo={() => {
                        navigate(ReplaceWords(routes.dashboardRroutePage, { route: routes.dashboardRroutes.buildings, id: building?.id }))
                    }}


                    
                />
            ),
        },
    ];

    return (
        <>
            <DialogModal
                size="3xl"
                isOpen={openResponsibleModal}
                onClose={() => setOpenResponsibleModal(false)}
                title={`${gW("add-responsible")}`}
                actions={[<Button variant="outline" disabled={loading} onClick={handleSaveResponsibles} >
                    {gW("save")}
                </Button>]}

            >

                {openResponsibleModal && <ResponsiblePage responsiblesIds={responsiblesIds} onChangeResponsibles={(e) => { setResponsiblesIds(e) }} />}
                {saving && <Loader />}
            </DialogModal>
            <div className="flex justify-between items-center mb-4">
                <h1 className="text-xl font-bold">{gW("buildings")}</h1>
                {user?.userType === UserTypes.MANAGER  && <Button onClick={() => setOpenResponsibleModal(true)} >
                    {gW("add-responsible")}
                </Button>}
            </div>


            {loading && <div className=" w-full p-16 flex justify-center items-center " >
                <Loader />
            </div>}

            {(!permission && !loading) && <Message className=" w-full " severity="warn" text={gW("no-permission")} />}

            {permission && !loading && <DataTable<Building>
                data={buildings}
                columns={columns}
                rowKey={(building) => (building?.id||"").toString()}
                loading={loading}
                totalRecords={totalRecords}
                rows={lazyParams.rows}
                first={lazyParams.first}
                onPageChange={onPageChange}
            />}

            <DialogModal
                size="2xl"
                isOpen={isPreviewOpen!==null}
                onClose={() => setIsPreviewOpen(null)}
                title={`${gW("preview")}: ${isPreviewOpen?.building?.title}`}
                
            >
                {isPreviewOpen && <PreviewBuilding data={isPreviewOpen} />}
            </DialogModal>
        </>
    );
};